import type { CSSProperties } from "react";

import {
  box,
  CORNER_CROSSES,
  floorGrid,
  IllustrationFrame,
  polyline,
  project,
  type Point3,
  type ProcessIllustration,
  type Shape,
} from "@/components/services/seo/seo-process-illustrations";

function pointAttrs(point: Point3) {
  const [cx, cy] = project(point);
  return { cx, cy };
}

// ---------------------------------------------------------------- 01 Strategy & range

/** A catalogue laid out on the floor: product boxes in rows, one picked out. */
const RANGE: Point3[] = [
  [16, 18, 10],
  [38, 18, 14],
  [60, 18, 10],
  [82, 18, 12],
  [16, 46, 12],
  [38, 46, 18],
  [60, 46, 12],
  [82, 46, 10],
  [27, 74, 10],
  [49, 74, 12],
  [71, 74, 14],
];

function RangeIllustration({ maskId }: { maskId: string | null }) {
  const shapes: Shape[] = [
    ...box(0, 0, 0, 100, 100, 4),
    ...RANGE.flatMap(([x, y, h]) => box(x - 7, y - 7, 4, 14, 14, h)),
  ];
  const picked = RANGE[5];

  return (
    <IllustrationFrame
      layers={[{ shapes }]}
      construct={floorGrid(100, 25)}
      crosses={CORNER_CROSSES}
      maskId={maskId}
    >
      {RANGE.map(([x, y, h], index) => (
        <circle
          key={index}
          className="cc-ill-ecom__node"
          style={{ "--cc-ill-node": index } as CSSProperties}
          {...pointAttrs([x, y, h + 4])}
          r={1.8}
        />
      ))}
      <circle className="cc-ill__dot" {...pointAttrs([picked[0], picked[1], picked[2] + 4])} r={3.2} />
    </IllustrationFrame>
  );
}

// ---------------------------------------------------------------- 02 Design & journey

/** A storefront with a big screen and the path from home to checkout. */
const JOURNEY: Point3[] = [
  [20, 60, 30],
  [40, 60, 38],
  [60, 60, 30],
  [80, 60, 40],
];

function DesignIllustration({ maskId }: { maskId: string | null }) {
  const shapes: Shape[] = [
    ...box(0, 20, 0, 100, 70, 4),
    ...box(10, 56, 4, 80, 8, 46),
    ...box(18, 44, 4, 16, 10, 8),
    ...box(66, 44, 4, 16, 10, 8),
  ];

  return (
    <IllustrationFrame
      layers={[{ shapes }]}
      construct={[
        polyline([[10, 56, 30], [90, 56, 30]]),
        polyline([[10, 56, 40], [90, 56, 40]]),
        polyline([[50, 56, 4], [50, 56, 50]]),
      ]}
      crosses={CORNER_CROSSES}
      maskId={maskId}
    >
      <path className="cc-ill-ecom__line" d={polyline(JOURNEY)} pathLength={1} />
      {JOURNEY.map((point, index) => (
        <circle
          key={index}
          className="cc-ill-ecom__node"
          style={{ "--cc-ill-node": index } as CSSProperties}
          {...pointAttrs(point)}
          r={2.6}
        />
      ))}
    </IllustrationFrame>
  );
}

// ---------------------------------------------------------------- 03 Build & integrations

/** The store in the middle, wired to payments, finance, stock and shipping. */
const HUB: Point3 = [50, 50, 30];
const SYSTEMS: Point3[] = [
  [16, 20, 16],
  [84, 20, 18],
  [16, 80, 14],
  [84, 80, 16],
];

function IntegrationsIllustration({ maskId }: { maskId: string | null }) {
  const shapes: Shape[] = [
    ...box(0, 0, 0, 100, 100, 4),
    ...box(36, 36, 4, 28, 28, 26),
    ...SYSTEMS.flatMap(([x, y, h]) => box(x - 8, y - 8, 4, 16, 16, h - 4)),
  ];

  return (
    <IllustrationFrame
      layers={[{ shapes }]}
      construct={[polyline([[0, 50, 4], [100, 50, 4]]), polyline([[50, 0, 4], [50, 100, 4]])]}
      crosses={CORNER_CROSSES}
      maskId={maskId}
    >
      <g className="cc-ill-ecom__links">
        {SYSTEMS.map((system, index) => (
          <path
            key={index}
            d={polyline([system, HUB])}
            pathLength={1}
            style={{ "--cc-ill-segment": index } as CSSProperties}
          />
        ))}
      </g>
      {SYSTEMS.map((system, index) => (
        <circle
          key={index}
          className="cc-ill-ecom__node"
          style={{ "--cc-ill-node": index } as CSSProperties}
          {...pointAttrs(system)}
          r={2.6}
        />
      ))}
      <circle className="cc-ill__dot" {...pointAttrs(HUB)} r={3.4} />
    </IllustrationFrame>
  );
}

// ---------------------------------------------------------------- 04 Launch & growth

const SALES: Point3[] = [
  [20, 50, 14],
  [36, 50, 18],
  [52, 50, 24],
  [68, 50, 32],
  [84, 50, 44],
];

function GrowthIllustration({ maskId }: { maskId: string | null }) {
  const shapes: Shape[] = [
    ...box(0, 30, 0, 100, 40, 4),
    ...SALES.flatMap(([x, , h]) => box(x - 5, 45, 4, 10, 10, h - 4)),
  ];
  const [lastX, lastY] = project(SALES[SALES.length - 1]);

  return (
    <IllustrationFrame
      layers={[{ shapes }]}
      construct={[14, 24, 34, 44].map((z) => polyline([[10, 45, z], [92, 45, z]]))}
      crosses={CORNER_CROSSES}
      maskId={maskId}
    >
      <path className="cc-ill-ecom__line" d={polyline(SALES)} pathLength={1} />
      <circle className="cc-ill-ecom__pulse" cx={lastX} cy={lastY} r={3} />
      <circle className="cc-ill__dot" cx={lastX} cy={lastY} r={2.5} />
    </IllustrationFrame>
  );
}

export const ECOM_PROCESS_ILLUSTRATIONS: ProcessIllustration[] = [
  RangeIllustration,
  DesignIllustration,
  IntegrationsIllustration,
  GrowthIllustration,
];
